import React, { useMemo, useState } from 'react';
import { get } from 'lodash';
import { useSelector } from 'react-redux';
import { Button as CvButton } from 'cvlib';
import ConfirmDeleteModal from 'components/ConfirmDeleteModal';
import ConfirmDeleteSuscriptionsModal from 'components/ConfirmDeleteSuscriptionsModal';
import AlertModal from 'components/AlertModal';
import { OnboardingLoading } from 'components/onboarding/components/OnboardingLoading';
import { GetRegion } from 'constants.js';
import useClaroPagos from 'containers/Payment/CreditCard/hook/useClaroPagos';
import {
  CP_CONFIG,
  CP_CONFIG_DELETE_CARD_HEADER,
  CP_CONFIG_UPDATE_HEADER,
} from 'containers/Payment/CreditCard/constants';
import { selectMetadataKeyByRegion } from 'reducers/selectors/metadata';
import { useTranslation } from 'hooks/useTranslation';
import {
  CARD_DETAIL_KEYS,
  CARD_DETAIL_BRAND_KEYS,
} from '../../translationKeys';
import { useSelectPaymentMethod } from '../../utils/useSelectPaymentMethod';
import { useInvalidateQrSession } from '../../utils/useInvalidateQrSession';
import { GATEWAY_CODES } from '../../constants';
import { SelectPaymentMethodButton } from '../SelectPaymentMethodButton';
import { useBackNavigationStep } from '../BackNavigation';
import { Button } from '../Button';
import { Card } from '../Card';
import './style.scss';

// Clon del borrado de containers/Payment/EditPaymentTDC/CardDetailScreen.js.
// El DELETE va contra Claro Pagos con el authorization de CP_CONFIG: el
// user_token de QR no aplica, Claro Pagos se autentica por su cuenta.
export const CardDetail = ({
  card,
  userToken,
  paymentMethodId,
  displayName,
  sessionUser,
  isMobileFlow,
  deviceParams,
  code,
  onBack,
  onEdit,
  onDeleted,
}) => {
  const { translationMapper } = useTranslation();
  const translatedTexts = useMemo(
    () => translationMapper(CARD_DETAIL_KEYS),
    [translationMapper],
  );
  const brandTranslatedTexts = useMemo(
    () => translationMapper(CARD_DETAIL_BRAND_KEYS, card?.marca),
    [translationMapper, card?.marca],
  );
  const { deleteCardFromUser } = useClaroPagos();
  const showPaymentSelector = useSelector(
    selectMetadataKeyByRegion('select_consulted_payment_method_config'),
  )?.enable;

  const [showConfirm, setShowConfirm] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [showConfirmDeleteSuscriptions, setShowConfirmDeleteSuscriptions] =
    useState(false);

  useBackNavigationStep(onBack);

  const cpUpdateHeaderToken = useSelector(state =>
    get(
      JSON.parse(get(state, CP_CONFIG, '{}')),
      `${GetRegion()}.${CP_CONFIG_UPDATE_HEADER}`,
      '',
    ),
  );
  const cpDeleteHeaderToken =
    useSelector(state =>
      get(
        JSON.parse(get(state, CP_CONFIG, '{}')),
        `${GetRegion()}.${CP_CONFIG_DELETE_CARD_HEADER}`,
        '',
      ),
    ) || '';

  const title = brandTranslatedTexts.TITLE;
  const editBtnText = translatedTexts.EDIT_BUTTON;
  const deleteBtnText = translatedTexts.DELETE_BUTTON;
  const intermittenceError = translatedTexts.INTERMITTENCE_ERROR;

  const handleDelete = async () => {
    try {
      await deleteCardFromUser(
        card?.token,
        atob(cpDeleteHeaderToken || cpUpdateHeaderToken),
      );
    } catch (error) {
      setShowConfirm(false);

      // Claro Pagos anida el código en el payload, no en el status HTTP —
      // a diferencia del detalle de PayPal, que mira error.response.status.
      if (get(error, 'response.data.error.code') === 412) {
        setShowConfirmDeleteSuscriptions(true);
      } else {
        setShowErrorAlert(true);
      }
      return;
    }

    setShowConfirm(false);
    onDeleted?.(card);
  };

  // DIVERGENCIA deliberada respecto al original: allí este confirmar navega a
  // /{region}/home/user?micuentafull, que en QR saca al usuario a una zona sin
  // sesión web y pierde location.state. Aquí solo se cierra el modal.
  const handleConfirmDeleteSuscriptions = () =>
    setShowConfirmDeleteSuscriptions(false);

  const { isSelecting, selectErrorMessage, clearSelectError, select } =
    useSelectPaymentMethod({
      sessionUser,
      isMobileFlow,
      userToken,
      intermittenceErrorMessage: intermittenceError,
      qrCode: code,
      deviceParams,
    });

  const { invalidateSession } = useInvalidateQrSession({
    sessionUser,
    isMobileFlow,
    code,
    deviceParams,
  });

  const handleCloseErrorAlert = () => {
    setShowErrorAlert(false);
    invalidateSession();
  };

  // Selecciona esta tarjeta como el instrumento de la transacción QR y cierra
  // la sesión de vinculación (useSelectPaymentMethod).
  const handleSelect = () =>
    select({
      id: paymentMethodId,
      code: GATEWAY_CODES.TDC,
      displayName,
      paymentMethodData: { token: card?.token, isDefault: true },
    });

  return (
    <div className="tdc-card-detail">
      <OnboardingLoading show={isSelecting} />

      <h1 className="tdc-card-detail__title">{title}</h1>

      <Card
        brand={card?.marca}
        number={card?.pan}
        expire={`${card?.expiracionMes}/${card?.expiracionAnio}`}
      />

      <div className="tdc-card-detail__actions">
        <Button className="tdc-card-detail__action" onClick={onEdit}>
          {editBtnText}
        </Button>

        <CvButton
          type="secondary"
          className="card-detail__delete"
          onClick={() => setShowConfirm(true)}
        >
          {deleteBtnText}
        </CvButton>
      </div>

      <SelectPaymentMethodButton
        enabled={showPaymentSelector}
        onClick={handleSelect}
      />

      <ConfirmDeleteModal
        show={showConfirm}
        handleDelete={handleDelete}
        setShow={setShowConfirm}
      />

      <AlertModal
        open={showErrorAlert}
        type="error"
        onClose={handleCloseErrorAlert}
        message={intermittenceError}
      />

      <AlertModal
        open={Boolean(selectErrorMessage)}
        type="error"
        onClose={clearSelectError}
        message={selectErrorMessage}
      />

      <ConfirmDeleteSuscriptionsModal
        show={showConfirmDeleteSuscriptions}
        setShow={setShowConfirmDeleteSuscriptions}
        handleConfirm={handleConfirmDeleteSuscriptions}
      />
    </div>
  );
};

export default CardDetail;
